import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, Upload, ArrowRight, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  importLeads,
  listFields,
  ImportRow,
  ImportResult,
  MarketingLeadField,
} from '@/services/marketingLeadService';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (result: ImportResult) => void;
}

type Step = 'upload' | 'mapping' | 'review' | 'done';

// Standard target fields the user can map to
interface TargetField {
  key: string;       // either base column ("firstName") or `customFields.<key>`
  label: string;
  required?: boolean;
  group: 'base' | 'custom';
}

const BASE_TARGETS: TargetField[] = [
  { key: 'email', label: 'Email', required: true, group: 'base' },
  { key: 'firstName', label: 'Prénom', group: 'base' },
  { key: 'lastName', label: 'Nom', group: 'base' },
  { key: 'fullName', label: 'Nom complet', group: 'base' },
  { key: 'phone', label: 'Téléphone', group: 'base' },
  { key: 'company', label: 'Société', group: 'base' },
  { key: 'jobTitle', label: 'Fonction', group: 'base' },
  { key: 'city', label: 'Ville', group: 'base' },
  { key: 'country', label: 'Pays', group: 'base' },
];

const SUGGESTIONS: Record<string, string> = {
  email: 'email', mail: 'email', courriel: 'email',
  firstname: 'firstName', prenom: 'firstName', 'first name': 'firstName', 'prénom': 'firstName',
  lastname: 'lastName', nom: 'lastName', 'last name': 'lastName', 'nom de famille': 'lastName',
  fullname: 'fullName', 'full name': 'fullName', 'nom complet': 'fullName',
  phone: 'phone', telephone: 'phone', 'téléphone': 'phone', tel: 'phone', mobile: 'phone',
  company: 'company', societe: 'company', 'société': 'company', entreprise: 'company',
  jobtitle: 'jobTitle', 'job title': 'jobTitle', poste: 'jobTitle', fonction: 'jobTitle',
  city: 'city', ville: 'city',
  country: 'country', pays: 'country',
};

const stripAccents = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();

export const CsvImportWizard: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({}); // header -> target key (or '__ignore')
  const [tagsRaw, setTagsRaw] = useState('');
  const [customFields, setCustomFields] = useState<MarketingLeadField[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Reset state
      setStep('upload');
      setFileName('');
      setHeaders([]);
      setRows([]);
      setMapping({});
      setTagsRaw('');
      setResult(null);
      setError(null);
      // Load custom fields so the mapper can show them
      listFields(true).then(setCustomFields).catch(() => setCustomFields([]));
    }
  }, [open]);

  const allTargets: TargetField[] = [
    ...BASE_TARGETS,
    ...customFields.map((f) => ({
      key: `customFields.${f.key}`,
      label: f.label,
      group: 'custom' as const,
    })),
  ];

  const handleFile = async (file: File) => {
    setError(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
      if (json.length === 0) {
        setError('Le fichier est vide.');
        return;
      }
      const cols = Object.keys(json[0]);
      const initialMap: Record<string, string> = {};
      cols.forEach((c) => {
        const guess = SUGGESTIONS[stripAccents(c)];
        initialMap[c] = guess ?? '__ignore';
      });
      setFileName(file.name);
      setHeaders(cols);
      setRows(json);
      setMapping(initialMap);
      setStep('mapping');
    } catch (e: any) {
      setError(e?.message ?? 'Impossible de lire le fichier.');
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const emailHeader = Object.entries(mapping).find(([, v]) => v === 'email')?.[0];

  const handleNextToReview = () => {
    if (!emailHeader) {
      setError('Vous devez mapper une colonne sur "Email".');
      return;
    }
    setError(null);
    setStep('review');
  };

  const buildPayloadRows = (): ImportRow[] => {
    return rows.map((r) => {
      const payload: ImportRow = { email: '' };
      const cf: Record<string, unknown> = {};
      for (const [header, target] of Object.entries(mapping)) {
        if (target === '__ignore') continue;
        const value = (r[header] ?? '').toString().trim();
        if (target.startsWith('customFields.')) {
          cf[target.slice('customFields.'.length)] = value;
        } else if (target === 'email') {
          payload.email = value;
        } else if (target === 'firstName') payload.firstName = value;
        else if (target === 'lastName') payload.lastName = value;
        else if (target === 'fullName') payload.fullName = value;
        else if (target === 'phone') payload.phone = value;
        else if (target === 'company') payload.company = value;
        else if (target === 'jobTitle') payload.jobTitle = value;
        else if (target === 'city') payload.city = value;
        else if (target === 'country') payload.country = value;
      }
      if (Object.keys(cf).length > 0) payload.customFields = cf;
      return payload;
    });
  };

  const handleCommit = async () => {
    setBusy(true);
    setError(null);
    try {
      const tags = tagsRaw
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const res = await importLeads({
        rows: buildPayloadRows(),
        fileName,
        mapping,
        tags,
      });
      setResult(res);
      setStep('done');
      onImported(res);
    } catch (e: any) {
      setError(e?.message ?? "Échec de l'import");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importer des leads</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Étape {step === 'upload' ? 1 : step === 'mapping' ? 2 : step === 'review' ? 3 : 4} / 4
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        {step === 'upload' && (
          <div className="p-8">
            <label className="border-2 border-dashed border-gray-300 rounded-xl py-12 px-6 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30">
              <Upload className="text-indigo-500 mb-3" size={36} />
              <p className="text-sm font-medium text-gray-900">Cliquez pour choisir un fichier</p>
              <p className="text-xs text-gray-500 mt-1">Excel (.xlsx, .xls) ou CSV (.csv)</p>
              <p className="text-xs text-gray-400 mt-2 italic">
                La première ligne doit contenir les titres de colonnes
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={onPickFile}
                className="hidden"
              />
            </label>
            {error && <p className="text-sm text-red-600 mt-3 text-center">{error}</p>}
          </div>
        )}

        {step === 'mapping' && (
          <div className="p-5 space-y-4">
            <div>
              <p className="text-sm text-gray-700">
                <strong>{rows.length}</strong> ligne{rows.length > 1 ? 's' : ''} détectée
                {rows.length > 1 ? 's' : ''} dans <code>{fileName}</code>.
              </p>
              <p className="text-xs text-gray-500">
                Indiquez à quel champ correspond chaque colonne. La colonne "Email" est obligatoire.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">Colonne du fichier</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">Aperçu (1ère ligne)</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-700">→ Champ lead</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {headers.map((h) => (
                    <tr key={h}>
                      <td className="px-3 py-2 text-gray-900 font-medium">{h}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-[200px]">
                        {String(rows[0]?.[h] ?? '')}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={mapping[h] ?? '__ignore'}
                          onChange={(e) =>
                            setMapping({ ...mapping, [h]: e.target.value })
                          }
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
                        >
                          <option value="__ignore">— Ignorer —</option>
                          <optgroup label="Champs standards">
                            {BASE_TARGETS.map((t) => (
                              <option key={t.key} value={t.key}>
                                {t.label}{t.required ? ' *' : ''}
                              </option>
                            ))}
                          </optgroup>
                          {customFields.length > 0 && (
                            <optgroup label="Champs personnalisés">
                              {customFields.map((f) => (
                                <option key={f.id} value={`customFields.${f.key}`}>
                                  {f.label}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags à appliquer (séparés par des virgules)
              </label>
              <input
                value={tagsRaw}
                onChange={(e) => setTagsRaw(e.target.value)}
                placeholder="ex: salon-2026, casablanca"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center gap-2">
                <AlertTriangle size={16} />
                {error}
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep('upload')}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Retour
              </button>
              <button
                onClick={handleNextToReview}
                disabled={!emailHeader}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                Vérifier <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="p-5 space-y-4">
            <div className="bg-blue-50 text-blue-800 text-sm p-3 rounded-lg">
              Prêt à importer <strong>{rows.length}</strong> ligne{rows.length > 1 ? 's' : ''}.
              Les emails déjà présents dans la liste des utilisateurs ou des leads existants seront ignorés
              automatiquement.
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Mappage choisi :</h4>
              <ul className="text-sm text-gray-700 list-disc list-inside space-y-0.5">
                {Object.entries(mapping)
                  .filter(([, v]) => v !== '__ignore')
                  .map(([h, v]) => {
                    const target = allTargets.find((t) => t.key === v);
                    return (
                      <li key={h}>
                        <code className="bg-gray-100 px-1 rounded">{h}</code> →{' '}
                        <strong>{target?.label ?? v}</strong>
                      </li>
                    );
                  })}
              </ul>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center gap-2">
                <AlertTriangle size={16} />
                {error}
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep('mapping')}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Retour
              </button>
              <button
                onClick={handleCommit}
                disabled={busy}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Importer
              </button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="p-8 text-center">
            <CheckCircle2 className="mx-auto mb-3 text-green-500" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Import terminé</h3>
            <p className="text-sm text-gray-600">
              <strong>{result.importedRows}</strong> lead(s) ajouté(s).
            </p>
            <ul className="mt-3 text-sm text-gray-600 inline-block text-left space-y-0.5">
              <li>• {result.skippedAsUser} ignoré(s) (déjà utilisateurs de la plateforme)</li>
              <li>• {result.skippedAsDuplicate} ignoré(s) (lead déjà existant)</li>
              <li>• {result.errorRows} ligne(s) en erreur</li>
            </ul>
            {result.errors.length > 0 && (
              <details className="mt-3 text-left max-w-md mx-auto">
                <summary className="cursor-pointer text-sm text-gray-500">
                  Voir le détail des erreurs
                </summary>
                <ul className="mt-2 text-xs text-gray-700 bg-gray-50 rounded p-2 max-h-48 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <li key={i}>Ligne {e.row}: {e.message}</li>
                  ))}
                </ul>
              </details>
            )}
            <button
              onClick={onClose}
              className="mt-6 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              Terminer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
